import React, { useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { Card } from '@/components/ui/Card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/Accordion';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/Alert';
import { Settings, HelpCircle, Info } from 'lucide-react';

export function DataDisplayComponents() {
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  return (
    <AppShell>
      <div className="p-6 space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-3xl font-semibold text-foreground mb-2">
            Data Display Components
          </h1>
          <p className="text-muted-foreground">
            Timeline, Tree, Popover, Sheet, Accordion, and Stepper components
          </p>
        </div>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            This page is being migrated to shadcn/ui components. Some components are temporarily unavailable.
          </AlertDescription>
        </Alert>

        {/* Popover */}
        <Card className="p-6">
          <h3 className="text-xl font-semibold text-foreground mb-6">
            Popover
          </h3>

          <div className="flex flex-wrap gap-4">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline">Open Popover</Button>
              </PopoverTrigger>
              <PopoverContent className="w-80">
                <div className="space-y-2">
                  <h4 className="font-medium text-foreground">
                    Popover Title
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    This is a popover component from shadcn/ui. It can contain any content.
                  </p>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </Card>

        {/* Sheet (Drawer) */}
        <Card className="p-6">
          <h3 className="text-xl font-semibold text-foreground mb-6">
            Sheet (Drawer)
          </h3>

          <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
            <SheetTrigger asChild>
              <Button>Open Sheet</Button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Sheet Title</SheetTitle>
                <SheetDescription>
                  This is a sheet component. It slides in from the right side of the screen.
                </SheetDescription>
              </SheetHeader>

              <div className="space-y-4 py-4">
                <p className="text-muted-foreground">
                  Sheet can contain any content and is useful for side panels, forms, and detail views.
                </p>

                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((item) => (
                    <div
                      key={item}
                      className="p-4 bg-muted rounded-md border"
                    >
                      <h4 className="font-medium text-foreground">
                        Item {item}
                      </h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Description for item {item}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <SheetFooter>
                <Button variant="outline" onClick={() => setIsSheetOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={() => setIsSheetOpen(false)}>
                  Save
                </Button>
              </SheetFooter>
            </SheetContent>
          </Sheet>
        </Card>

        {/* Accordion */}
        <Card className="p-6">
          <h3 className="text-xl font-semibold text-foreground mb-6">
            Accordion
          </h3>

          <Accordion type="multiple" className="w-full" defaultValue={['item-1']}>
            <AccordionItem value="item-1">
              <AccordionTrigger>
                <div className="flex items-center gap-2">
                  <HelpCircle className="w-5 h-5" />
                  <span>What is SimCheck?</span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                SimCheck is a comprehensive telecom testing platform designed for operations teams to
                manage and test modem infrastructure. It provides real-time monitoring, automated test
                flows, and detailed analytics.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2">
              <AccordionTrigger>
                <div className="flex items-center gap-2">
                  <Info className="w-5 h-5" />
                  <span>How do I create a flow?</span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                To create a flow, navigate to the Flows page, click "Create Flow", and follow the
                step-by-step wizard. You can define test steps, configure modem connections, and set
                up validation criteria.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3">
              <AccordionTrigger>
                <div className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  <span>API Integration</span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                SimCheck provides a comprehensive REST API for integration with your existing tools.
                You can manage flows, execute tests, and retrieve results programmatically. Check the
                API documentation in Settings → Integrations for more details.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Card>

        {/* Coming Soon Section */}
        <Card className="p-6">
          <h3 className="text-xl font-semibold text-foreground mb-4">
            Coming Soon
          </h3>
          <p className="text-muted-foreground">
            The following components are being migrated to shadcn/ui equivalents:
          </p>
          <ul className="mt-4 space-y-2 text-sm text-muted-foreground list-disc list-inside">
            <li>Timeline (evaluating shadcn alternatives)</li>
            <li>Tree View (evaluating tree component libraries)</li>
            <li>Stepper (evaluating step indicator patterns)</li>
          </ul>
        </Card>
      </div>
    </AppShell>
  );
}
